import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { useProjection, useLoadCurves } from '@/hooks/useClosedLoopAnalytics';
import { MetricInfoButton } from '@/components/MetricInfoButton';

interface FitnessProjectionChartProps {
  className?: string;
}

interface ProjectionTooltipEntry {
  name?: string;
  value?: number;
  color?: string;
}

function ProjectionTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: ProjectionTooltipEntry[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2.5 rounded-lg border border-white/[0.12] bg-[rgba(12,16,24,0.92)] backdrop-blur-[20px] shadow-lg">
      <p className="text-[10px] font-mono tracking-[0.1em] text-muted-foreground mb-1.5 uppercase">
        {label}
      </p>
      {payload.map((entry: ProjectionTooltipEntry, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-[10px] font-mono text-muted-foreground">{entry.name}:</span>
          <span className="text-xs font-mono tabular-nums text-foreground">
            {typeof entry.value === 'number' ? entry.value.toFixed(1) : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

function fmt(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'MMM d');
  } catch {
    return dateStr.slice(5);
  }
}

export function FitnessProjectionChart({ className }: FitnessProjectionChartProps) {
  const { data: loadCurves } = useLoadCurves();
  const { data: projection, isLoading } = useProjection(8);

  const chartData = useMemo(() => {
    if (!projection?.projected_ctl?.length) return [];

    // Get last 30 days of historical data
    const historical = (loadCurves?.points ?? []).slice(-30).map((p) => ({
      date: p.date,
      dateLabel: fmt(p.date),
      historicalCtl: p.ctl,
      historicalTsb: p.tsb,
      projectedCtl: null as number | null,
      projectedTsb: null as number | null,
    }));

    // Bridge point: last historical = first projected
    const lastHist = historical[historical.length - 1];

    const projected = projection.projected_ctl.map((p) => ({
      date: p.date,
      dateLabel: fmt(p.date),
      historicalCtl: null as number | null,
      historicalTsb: null as number | null,
      projectedCtl: p.ctl,
      projectedTsb: p.tsb,
    }));

    // Connect the two series at the bridge point
    if (lastHist && projected.length > 0) {
      projected[0].historicalCtl = lastHist.historicalCtl;
      projected[0].historicalTsb = lastHist.historicalTsb;
    }

    return [...historical, ...projected];
  }, [loadCurves, projection]);

  const projectionStartLabel = useMemo(() => {
    const pts = loadCurves?.points ?? [];
    if (pts.length === 0) return undefined;
    return fmt(pts[pts.length - 1].date);
  }, [loadCurves]);

  if (isLoading) {
    return (
      <div className={cn('p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]', className)}>
        <p className="text-xs font-mono text-muted-foreground text-center animate-pulse">LOADING PROJECTION...</p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div className={cn(
      'p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]',
      'shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]',
      className,
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
          <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
            FITNESS PROJECTION
          </span>
          <MetricInfoButton metricKey="fitnessProjection" />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-[2px] rounded bg-[hsl(170,80%,45%)]" />
            <span className="text-[9px] font-mono text-muted-foreground">CTL</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-[2px] rounded bg-[hsl(200,65%,55%)]" style={{ borderBottom: '1px dashed' }} />
            <span className="text-[9px] font-mono text-muted-foreground">TSB</span>
          </div>
        </div>
      </div>

      {/* Peak CTL + race day stats */}
      {projection && (
        <div className="flex items-center gap-4 mb-3">
          <div>
            <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Peak CTL</span>
            <span className="ml-1.5 text-sm font-mono tabular-nums text-[hsl(170,80%,45%)]">
              {projection.peak_ctl?.toFixed(1) ?? '—'}
            </span>
          </div>
          {projection.race_day_ctl != null && (
            <div>
              <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Race Day</span>
              <span className="ml-1.5 text-sm font-mono tabular-nums text-foreground">
                CTL {projection.race_day_ctl.toFixed(1)}
              </span>
              {projection.race_day_tsb != null && (
                <span className={cn(
                  'ml-1 text-sm font-mono tabular-nums',
                  projection.race_day_tsb >= 0 ? 'text-emerald-400' : 'text-amber-400',
                )}>
                  TSB {projection.race_day_tsb.toFixed(1)}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Chart */}
      <div className="h-52 sm:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis
              dataKey="dateLabel"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.25)', fontFamily: 'monospace' }}
              width={35}
            />
            <Tooltip content={<ProjectionTooltip />} />

            {projectionStartLabel && (
              <ReferenceLine
                x={projectionStartLabel}
                stroke="rgba(255,255,255,0.15)"
                strokeDasharray="3 3"
                label={{ value: 'NOW', fontSize: 9, fill: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}
              />
            )}
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 4" />

            {/* Historical CTL (solid) */}
            <Line
              type="monotone"
              dataKey="historicalCtl"
              stroke="hsl(170, 80%, 45%)"
              strokeWidth={2}
              dot={false}
              name="CTL (actual)"
              connectNulls={false}
            />
            {/* Projected CTL (dashed) */}
            <Line
              type="monotone"
              dataKey="projectedCtl"
              stroke="hsl(170, 80%, 45%)"
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={false}
              name="CTL (projected)"
              connectNulls={false}
            />
            {/* Historical TSB (solid) */}
            <Line
              type="monotone"
              dataKey="historicalTsb"
              stroke="hsl(200, 65%, 55%)"
              strokeWidth={1.5}
              dot={false}
              name="TSB (actual)"
              connectNulls={false}
            />
            {/* Projected TSB (dashed) */}
            <Line
              type="monotone"
              dataKey="projectedTsb"
              stroke="hsl(200, 65%, 55%)"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              dot={false}
              name="TSB (projected)"
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-[9px] font-mono text-white/25 tracking-wider text-center mt-2 uppercase">
        Solid = actual · Dashed = projected from planned training
      </p>
    </div>
  );
}
